import React from 'react'
import {
	Box,
	Typography,
	Table,
	TableBody,
	TableContainer,
	TableHead,
	TableRow,
	IconButton,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { Constraint } from '../../../../../../types/Work/Constraint'
import { StyledTableCell } from '../../../../../core/StyledTableCell'
import SearchBar from '../../../../../core/SearchBar'
import { formatLocaleShortDateTime } from '../../../../../../services/CommonServices/DataFormatters'
import { useProjectFeaturesContext } from '../../../../../../components/project/contexts/ProjectFeaturesContext'

type ConstraintsTabProps = {
	constraints: Constraint[]
	onAddTask: (taskId: number, name: string, activityType: 'constraint') => void
	searchText: string
	handleSearchTextChange: (searchText: string) => void
}

const ConstraintsTab = ({
	constraints,
	onAddTask,
	searchText,
	handleSearchTextChange,
}: ConstraintsTabProps) => {
	const {projectTimeZone} = useProjectFeaturesContext()

	const handleAddConstraint = (constraint: Constraint) => {
		onAddTask(constraint.id, `Constraint: ${constraint.comments}`, 'constraint')
	}

	return (
		<Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
			<SearchBar
				onChange={handleSearchTextChange}
				searchPlaceholder='Filter by comment or work item name'
				showIncludeFilter={false}
				searchText={searchText}
			/>
			{constraints.length === 0 ? (
				<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
					<Typography variant='body1'>No constraints found.</Typography>
				</Box>
			) : (
				<TableContainer sx={{ maxHeight: '100%', overflowY: 'auto' }}>
					<Table stickyHeader>
						<TableHead>
							<TableRow>
								<StyledTableCell />
								<StyledTableCell align='left'>Comment</StyledTableCell>
								<StyledTableCell align='left'>Reported By</StyledTableCell>
								<StyledTableCell align='left'>Time Reported</StyledTableCell>
								<StyledTableCell align='left'>Work Item Name</StyledTableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{constraints.map((constraint) => {
								const workItemParts = constraint.workItemName.split(' > ')
								return (
									<TableRow key={constraint.id} hover>
										<StyledTableCell sx={{ paddingRight: '0' }}>
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
												<IconButton
													aria-label='add constraint'
													size='small'
													onClick={() => handleAddConstraint(constraint)}
													color='primary'
												>
													<AddIcon />
												</IconButton>
											</Box>
										</StyledTableCell>
										<StyledTableCell align='left'>{constraint.comments}</StyledTableCell>
										<StyledTableCell align='left'>{constraint.reportedByName}</StyledTableCell>
										<StyledTableCell align='left'>
											{formatLocaleShortDateTime(constraint.timeReported, projectTimeZone ?? 'UTC')}
										</StyledTableCell>
										<StyledTableCell align='left'>
											{workItemParts.map((part, index) => (
												<Typography
													key={index}
													variant='body2'
													sx={{
														wordBreak: 'break-word',
														mb: index < workItemParts.length - 1 ? 0.5 : 0,
													}}
												>
													{part}{index < workItemParts.length - 1 ? ' >' : ''}
												</Typography>
											))}
										</StyledTableCell>
									</TableRow>
								)
							})}
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</Box>
	)
}

export default ConstraintsTab
